import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import type { Country, CountryCode, Event, EventKind, Match, Variant, Venue } from '@/types/domain';
import { changePin as apiChangePin } from '@/api/account';
import { AccountSheet } from '@/components/AccountSheet';
import { LivePulseDot } from '@/components/LiveState';
import { Flag, FlagStripe, SaveToggle, VariantHeading } from '@/components/primitives';
import { SharePlanFlow, type SharePlanRequest } from '@/components/SharePlanFlow';
import { EVENTS, track } from '@/lib/analytics';
import { haptics } from '@/lib/native';
import { useInstallation } from '@/providers/InstallationProvider';
import { formatKickoffDate, formatKickoffTime } from '@/utils/time';

interface SavedScreenProps {
  variant: Variant;
  countriesByCode: Partial<Record<CountryCode, Country>>;
  matches: Match[];
  /** venueId → matchId of the active plan. Truthiness == "going". */
  going: Record<string, string | undefined>;
  /** Bookmarked venues, server-ordered newest-first. */
  savedVenues: Venue[];
  /** Flat venueId → Venue lookup from the store. */
  venuesById: Record<string, Venue>;
  /** Full public events feed (already hydrated by App bootstrap).
   * Used to resolve the `eventRsvps` ID set into rich rows. */
  events: Event[];
  /** Event IDs the user has RSVP'd to. */
  eventRsvps: Set<string>;
  /** Trigger a lazy fetch for a country whose venues haven't loaded
   * yet. The store dedupes, so it's safe to call repeatedly. */
  loadVenuesForCountry: (code: CountryCode) => Promise<Venue[]>;
  onPickMatch: (match: Match) => void;
  onPickVenue: (venue: Venue) => void;
  onPickEvent: (event: Event) => void;
  onToggleSave: (venue: Venue) => void;
  onToggleRsvp: (eventId: string) => void;
}

interface PlanRow {
  key: string;
  match: Match;
  venue: Venue;
  venueCountry: Country;
}

// Bracket-position placeholders the backend (or legacy data) may put in
// `match.home/away` when a knockout slot isn't decided yet:
//   - "1A" / "2B" / "3C"            group rank + slot
//   - "1A/2B"                       group winner OR runner-up pairing
//   - "W31" / "L4"                  winner/loser of a previous game number
// Requires a leading digit or a W/L+digit so it can't false-match real
// 3-letter country codes — the old looser pattern matched any 3 caps in
// A–L, so "CHI", "BEL", "ALG", "GHA", "HAI", "BIH" got mistreated as
// placeholders and lost their flag.
const BRACKET_PLACEHOLDER = /^(?:[1-3][A-L](?:\/[1-3][A-L])?|[WL]\d+)$/;

type SavedFilter = 'all' | 'matches' | 'events' | 'bars';

const FILTER_TABS: { id: SavedFilter; labelKey: string }[] = [
  { id: 'all',     labelKey: 'saved:tabs.all' },
  { id: 'matches', labelKey: 'saved:tabs.matches' },
  { id: 'events',  labelKey: 'saved:tabs.events' },
  { id: 'bars',    labelKey: 'saved:tabs.bars' },
];

// Mirrors the EventsScreen KIND map so a parade row reads as parade and a
// watch row reads as watch on Saved. Kept inline rather than imported from
// EventsScreen to avoid cross-screen coupling; the union is small and rarely
// changes. If a third surface ever wants it, hoist to a shared module.
const EVENT_KIND_DOT: Record<EventKind, string> = {
  watch:   '#d23',
  fanfest: '#0a7',
  village: '#0366d6',
  food:    '#c80',
  parade:  '#d8c',
  after:   '#5a4a3c',
};

const TZ = 'America/New_York';

function formatEventTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const SHARE_BUTTON_STYLE: CSSProperties = {
  position: 'absolute',
  top: 10,
  right: 10,
  width: 36,
  height: 36,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderRadius: 8,
};

// Keyframes CSS for the share-capture spinner. Not language — a stylesheet
// string rendered into a <style> tag, kept in a const so it stays out of the
// i18n literal guard.
const SHARE_SPIN_KEYFRAMES = '@keyframes nynjwc-share-spin { to { transform: rotate(360deg); } }';

const SHARE_SPINNER_STYLE: CSSProperties = {
  width: 14,
  height: 14,
  border: '2px solid #e5e1d8',
  borderTopColor: '#9a9590',
  borderRadius: '50%',
  animation: 'nynjwc-share-spin 0.8s linear infinite',
};

const ACCOUNT_GLYPH = '✦';

const accountLinkBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#5a544c',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  flexShrink: 0,
  padding: 4,
};

const accountBannerBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '14px 16px',
  background: '#faf7f2',
  border: '1px solid #ece8e2',
  borderRadius: 14,
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: '-apple-system, system-ui',
};

export function SavedScreen({
  variant,
  countriesByCode,
  matches,
  going,
  savedVenues,
  venuesById,
  events,
  eventRsvps,
  loadVenuesForCountry,
  onPickMatch,
  onPickVenue,
  onPickEvent,
  onToggleSave,
  onToggleRsvp,
}: SavedScreenProps) {
  const { t } = useTranslation();
  const { username, isSignedIn, claimAccount, signIn, signOut } = useInstallation();
  const [accountSheet, setAccountSheet] = useState<null | 'create' | 'changePin'>(null);
  const [filter, setFilter] = useState<SavedFilter>('all');
  // "Earlier" (past matches/events) sits at the very bottom and starts
  // collapsed — it's memory, not a to-do, so it shouldn't push live plans down.
  const [earlierOpen, setEarlierOpen] = useState(false);

  const matchById = useMemo(() => {
    const m: Record<string, Match> = {};
    for (const x of matches) m[x.id] = x;
    return m;
  }, [matches]);

  // The plan currently being shared. Set it to open the unified share flow
  // (capture → preview → native share); SharePlanFlow clears it via onClose.
  const [shareRequest, setShareRequest] = useState<SharePlanRequest | null>(null);
  const shareBusy = shareRequest !== null;

  // Trigger lazy loads for the countries whose venues we'll need to
  // resolve plan rows. We don't know a venue's country until it's
  // loaded, so we drive the load off the *match* metadata (home + away
  // codes are always known up front).
  useEffect(() => {
    const codes = new Set<CountryCode>();
    for (const venueId of Object.keys(going)) {
      const mid = going[venueId];
      if (!mid) continue;
      const match = matchById[mid];
      if (!match) continue;
      if (match.home) codes.add(match.home);
      if (match.away) codes.add(match.away);
    }
    for (const code of codes) void loadVenuesForCountry(code);
  }, [going, matchById, loadVenuesForCountry]);

  // Index venue → country via the store's venuesById. Plan and saved-venue
  // rows both use this for palettes. Entries are absent until the
  // owning country's venues have loaded — callers skip those rows
  // rather than render half-resolved.
  const countryByVenueId = useMemo(() => {
    const m: Record<string, Country> = {};
    for (const v of Object.values(venuesById)) {
      const c = countriesByCode[v.countryCode];
      if (c) m[v.id] = c;
    }
    return m;
  }, [venuesById, countriesByCode]);

  // Build (match, venue) rows from the going map. One entry per venue we're
  // going to; the spike's invariant is one venue per match, but defensively
  // tolerate dupes if data drifts. Rows whose venue/country aren't loaded
  // yet are skipped — the useEffect above will trigger the loads, and the
  // component re-renders once they arrive.
  const planRows: PlanRow[] = [];
  for (const [venueId, mid] of Object.entries(going)) {
    if (!mid) continue;
    const venue = venuesById[venueId];
    if (!venue) continue;
    const venueCountry = countriesByCode[venue.countryCode];
    if (!venueCountry) continue;
    const match = matchById[mid];
    if (!match) continue;
    planRows.push({ key: `${mid}:${venueId}`, match, venue, venueCountry });
  }
  // Sort upcoming first, then split past into "Earlier".
  const now = Date.now();
  planRows.sort((a, b) => Date.parse(a.match.kickoffAt) - Date.parse(b.match.kickoffAt));
  const upcoming = planRows.filter((r) => Date.parse(r.match.kickoffAt) >= now || r.match.live);
  const earlier = planRows.filter((r) => Date.parse(r.match.kickoffAt) < now && !r.match.live);

  // Resolve RSVP'd event IDs against the public events feed already
  // hydrated by App bootstrap. Past events are kept by design (memory >
  // tidiness — users look up "what bar was that ARG party at?"); they
  // get folded into the "Earlier" group below. Sort ascending so the
  // soonest commitment is on top.
  const eventRows = useMemo(
    () =>
      events
        .filter((e) => eventRsvps.has(e.id))
        .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt)),
    [events, eventRsvps],
  );
  const upcomingEvents = eventRows.filter((e) => Date.parse(e.startsAt) >= now);
  const earlierEvents  = eventRows.filter((e) => Date.parse(e.startsAt) <  now);

  const hasPlans  = planRows.length > 0;
  const hasEvents = eventRows.length > 0;
  const hasSaved  = savedVenues.length > 0;
  const isEmpty   = !hasPlans && !hasEvents && !hasSaved;

  // Filter-aware visibility. Each show flag is true when the current filter
  // includes that type. 'all' includes everything; type-specific filters
  // include only their own type.
  const showMatches = filter === 'all' || filter === 'matches';
  const showEvents  = filter === 'all' || filter === 'events';
  const showBars    = filter === 'all' || filter === 'bars';

  // Section headings only render in 'all' mode — when filtered to one type,
  // the segmented-control selection IS the heading; duplicating it inline
  // would be noise.
  const showSectionHeadings = filter === 'all';

  const hasEarlier =
    (showMatches && earlier.length > 0) || (showEvents && earlierEvents.length > 0);
  const earlierCount =
    (showMatches ? earlier.length : 0) + (showEvents ? earlierEvents.length : 0);

  // What the current filter actually has to show (drives per-filter empty
  // messages). 'all' already routes through the global isEmpty above.
  const filterHasContent =
    (filter === 'all'     && (hasPlans || hasEvents || hasSaved))
    || (filter === 'matches' && hasPlans)
    || (filter === 'events'  && hasEvents)
    || (filter === 'bars'    && hasSaved);

  function Header() {
    if (variant === 'editorial') {
      return (
        <div style={{ padding: 'max(28px, env(safe-area-inset-top)) 24px 16px', background: '#fbf8f3' }}>
          <div
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: 44,
              color: '#1a1612',
              letterSpacing: -1.2,
              lineHeight: 1,
              fontWeight: 400,
              fontStyle: 'italic',
            }}
          >
            {t('saved:titleEditorial')}
          </div>
        </div>
      );
    }
    if (variant === 'minimal') {
      return (
        <div style={{ padding: 'max(28px, env(safe-area-inset-top)) 20px 12px' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#0a0a0a', letterSpacing: -0.5 }}>
            {t('saved:titleMinimal')}
          </div>
        </div>
      );
    }
    return (
      <div style={{ padding: 'max(24px, env(safe-area-inset-top)) 20px 14px' }}>
        <div
          style={{
            fontSize: 32,
            fontWeight: 900,
            color: '#0a0a0a',
            letterSpacing: -0.8,
          }}
        >
          {t('saved:title')}
        </div>
      </div>
    );
  }

  const accountStrip = isSignedIn ? (
    <div style={{ padding: '0 20px 4px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          background: '#faf7f2',
          border: '1px solid #ece8e2',
          borderRadius: 14,
        }}
      >
        <span aria-hidden="true" style={{ color: '#d23', fontSize: 14, flexShrink: 0 }}>
          {ACCOUNT_GLYPH}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 700,
            color: '#1a1612',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t('saved:account.signedInAs', { username: username ?? '' })}
        </span>
        <button type="button" onClick={() => setAccountSheet('changePin')} style={accountLinkBtn}>
          {t('saved:account.changePin')}
        </button>
        <button type="button" onClick={() => void signOut()} style={accountLinkBtn}>
          {t('saved:account.signOut')}
        </button>
      </div>
    </div>
  ) : (
    <div style={{ padding: '0 20px 4px' }}>
      <button type="button" onClick={() => setAccountSheet('create')} style={accountBannerBtn}>
        <span aria-hidden="true" style={{ color: '#d23', fontSize: 15, flexShrink: 0 }}>
          {ACCOUNT_GLYPH}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{ display: 'block', fontSize: 14, fontWeight: 800, color: '#1a1612', lineHeight: 1.25 }}
          >
            {t('saved:account.bannerHeadline')}
          </span>
          <span
            style={{ display: 'block', fontSize: 12, color: '#5a544c', marginTop: 2, lineHeight: 1.35 }}
          >
            {t('saved:account.bannerSub')}
          </span>
        </span>
        <span
          style={{ fontSize: 13, fontWeight: 700, color: '#d23', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('saved:account.bannerCta')}
        </span>
      </button>
    </div>
  );

  const accountSheetEl = accountSheet ? (
    <AccountSheet
      mode={accountSheet}
      onClose={() => setAccountSheet(null)}
      onClaim={claimAccount}
      onSignIn={signIn}
      onChangePin={apiChangePin}
      t={t}
    />
  ) : null;

  if (isEmpty) {
    return (
      <>
        <Header />
        {accountStrip}
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          <div
            style={{
              fontFamily:
                variant === 'editorial' ? '"Instrument Serif", Georgia, serif' : '-apple-system, system-ui',
              fontSize: variant === 'editorial' ? 20 : 15,
              color: '#5a544c',
              fontStyle: variant === 'editorial' ? 'italic' : 'normal',
              lineHeight: 1.5,
            }}
          >
            {t('saved:emptyLine1')}
            <br />
            {t('saved:emptyLine2')}
            <br />
            {t('saved:emptyLine3')}
          </div>
        </div>
        {accountSheetEl}
      </>
    );
  }

  function SegmentedControl() {
    return (
      <div
        role="tablist"
        aria-label={t('saved:filterAriaLabel')}
        style={{
          margin: '0 20px 18px',
          background: '#ece8e2',
          borderRadius: 999,
          padding: 4,
          display: 'flex',
          gap: 0,
        }}
      >
        {FILTER_TABS.map((tab) => {
          const active = filter === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(tab.id)}
              style={{
                flex: 1,
                background: active ? '#fff' : 'transparent',
                color: active ? '#0a0a0a' : '#5a544c',
                padding: '8px 0',
                borderRadius: 999,
                border: 'none',
                fontFamily: '-apple-system, system-ui',
                fontSize: 13,
                fontWeight: active ? 700 : 600,
                letterSpacing: 0.1,
                // Subtle lift on the active pill — iOS segmented control
                // signature. Transition smooths the slide between tabs.
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease',
                cursor: 'pointer',
              }}
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>
    );
  }

  // Per-filter empty message — shown when the current filter has nothing
  // even though the screen overall has content. Single line; the segmented
  // control above already names what the user filtered to.
  const filterEmptyMessage: Record<SavedFilter, string> = {
    all:     '',
    matches: t('saved:emptyMessage.matches'),
    events:  t('saved:emptyMessage.events'),
    bars:    t('saved:emptyMessage.bars'),
  };

  function renderPlanRow({ key, match, venue, venueCountry }: PlanRow) {
    const home = match.home ? countriesByCode[match.home] : undefined;
    const away = match.away ? countriesByCode[match.away] : undefined;
    // "Placeholder" only means "TBD bracket slot" (null code, or a bracket
    // pattern like '1A' / 'W1'). Don't treat non-curated countries as
    // placeholders — every code in match.home/away has a real flag SVG via
    // FLAG_COMPONENTS even if the country isn't in the followable set.
    const homeIsPlaceholder =
      match.home === null || BRACKET_PLACEHOLDER.test(match.home);
    const awayIsPlaceholder =
      match.away === null || BRACKET_PLACEHOLDER.test(match.away);
    const palette = venueCountry.colors;

    const isCapturing =
      shareRequest?.match.id === match.id && shareRequest?.venue.id === venue.id;
    return (
      <div key={key} style={{ position: 'relative', marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => onPickMatch(match)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            background: '#fff',
            border: '1px solid #f0ede8',
            borderRadius: 14,
            padding: '14px 16px',
            paddingRight: 52,
            cursor: 'pointer',
          }}
        >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
            fontSize: 11,
            fontWeight: 700,
            color: '#9a9590',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            fontFamily: 'ui-monospace, monospace',
          }}
        >
          <span>{formatKickoffDate(match.kickoffAt)}</span>
          <span>·</span>
          <span>{formatKickoffTime(match.kickoffAt)}</span>
          {match.status === 'LIVE' && (
            <span
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 6px',
                borderRadius: 4,
                background: '#d1393a',
                color: '#fff',
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: 0.5,
              }}
            >
              <LivePulseDot size={5} color="#fff" />
              {t('common:live')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#0a0a0a' }}>
            {match.home ?? match.homeLabel ?? t('common:tbd')}
          </span>
          {homeIsPlaceholder || !match.home ? (
            <FlagStripe colors={palette} w={20} h={14} radius={2} />
          ) : (
            <Flag code={match.home} w={20} h={14} radius={2} />
          )}
          <span style={{ fontSize: 11, color: '#9a9590', fontWeight: 600 }}>{t('common:vs')}</span>
          {awayIsPlaceholder || !match.away ? (
            <FlagStripe colors={palette} w={20} h={14} radius={2} />
          ) : (
            <Flag code={match.away} w={20} h={14} radius={2} />
          )}
          <span style={{ fontSize: 13, fontWeight: 800, color: '#0a0a0a' }}>
            {match.away ?? match.awayLabel ?? t('common:tbd')}
          </span>
        </div>
        {/* Plain text flow, not flex — a flex row with no wrap squeezes each
            child into its own column and breaks "Going to" mid-phrase. As
            inline text it wraps naturally: the label and hood stay intact
            (nowrap), only the venue name wraps word-by-word if it must. */}
        <div style={{ fontSize: 13, lineHeight: 1.45 }}>
          <span style={{ color: '#5a544c', whiteSpace: 'nowrap' }}>{t('saved:goingTo')}</span>{' '}
          <span style={{ fontWeight: 700, color: '#0a0a0a' }}>{venue.name}</span>
          <span style={{ color: '#9a9590', fontSize: 12, whiteSpace: 'nowrap' }}>
            {' '}· {venue.hood}
          </span>
        </div>
        </button>
        <button
          type="button"
          aria-label={t('saved:shareGoing', { venue: venue.name })}
          disabled={shareBusy}
          onClick={() => {
            // Count Saved-page share taps. Generic `share_pressed` + a
            // `surface` prop so other share buttons (map, match) can roll up
            // under one event, filterable by surface.
            track(EVENTS.sharePressed, {
              surface: 'saved',
              matchId: match.id,
              venueId: venue.id,
              countryCode: venue.countryCode,
            });
            setShareRequest({ match, venue, venueCountry, homeCountry: home, awayCountry: away });
          }}
          style={{ ...SHARE_BUTTON_STYLE, opacity: shareBusy && !isCapturing ? 0.4 : 1 }}
        >
          {isCapturing ? (
            <div style={SHARE_SPINNER_STYLE} />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9a9590" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v13" />
              <path d="m7 8 5-5 5 5" />
              <path d="M5 21h14a2 2 0 0 0 2-2v-7" />
              <path d="M3 12v7a2 2 0 0 0 2 2" />
            </svg>
          )}
        </button>
      </div>
    );
  }

  function renderEventRow(ev: Event) {
    const dot = EVENT_KIND_DOT[ev.kind];
    const venue = ev.venueId ? venuesById[ev.venueId] : undefined;
    const venueName = venue?.name ?? ev.venueName ?? '';
    const hood = venue?.hood ?? ev.venueHood ?? '';
    return (
      <div key={ev.id} style={{ position: 'relative', marginBottom: 10 }}>
        <button
          type="button"
          // Placeholder: drops the user on the Events tab. Will be re-pointed
          // at an EventDetailScreen once scraped event metadata lands.
          onClick={() => onPickEvent(ev)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            background: '#fff',
            border: '1px solid #f0ede8',
            borderRadius: 14,
            padding: '14px 16px',
            paddingRight: 76,
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
              fontSize: 11,
              fontWeight: 700,
              color: '#9a9590',
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            <span>{formatKickoffDate(ev.startsAt)}</span>
            <span>·</span>
            <span>{formatEventTime(ev.startsAt)}</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 4,
            }}
          >
            <span
              aria-hidden
              style={{ width: 8, height: 8, borderRadius: 4, background: dot, flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, fontWeight: 800, color: '#0a0a0a', lineHeight: 1.25 }}>
              {ev.title}
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#5a544c' }}>
            {venueName}
            {hood ? ` · ${hood}` : ''}
          </div>
        </button>
        <button
          type="button"
          aria-label={t('saved:notGoing', { event: ev.title })}
          aria-pressed
          onClick={() => {
            haptics.light();
            onToggleRsvp(ev.id);
          }}
          style={{
            position: 'absolute',
            // Vertically centered against the row body — reads as a commitment
            // status sitting alongside the title, not a top-corner metadata tag.
            top: '50%',
            right: 12,
            transform: 'translateY(-50%)',
            // Green "going" pill matching the Events page RSVP state (SST-1) —
            // every event here is one you RSVP'd to, so the chip confirms it.
            border: 'none',
            background: '#0a7f0a',
            color: '#fff',
            padding: '5px 11px',
            borderRadius: 999,
            fontFamily: '-apple-system, system-ui',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            cursor: 'pointer',
            lineHeight: 1.2,
            boxShadow: '0 3px 10px #0a7f0a55',
          }}
        >
          {t('common:goingCheck')}
        </button>
      </div>
    );
  }

  function renderSavedVenue(venue: Venue) {
    const country = countryByVenueId[venue.id];
    return (
      <div
        key={venue.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: '#fff',
          border: '1px solid #f0ede8',
          borderRadius: 14,
          padding: '12px 14px',
          marginBottom: 10,
        }}
      >
        <button
          type="button"
          onClick={() => onPickVenue(venue)}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            padding: 0,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            {country ? (
              <Flag code={country.code} w={16} h={11} radius={2} />
            ) : null}
            <span style={{ fontSize: 15, fontWeight: 700, color: '#0a0a0a' }}>{venue.name}</span>
          </div>
          <div style={{ fontSize: 12, color: '#9a9590' }}>
            {venue.type} · {venue.hood}
          </div>
        </button>
        <SaveToggle
          saved
          onToggle={() => onToggleSave(venue)}
          venueName={venue.name}
        />
      </div>
    );
  }

  return (
    <>
      <Header />
      {accountStrip}
      <SegmentedControl />
      <div style={{ padding: '0 20px 24px' }}>
        {!filterHasContent && (
          <div
            style={{
              padding: '32px 24px',
              textAlign: 'center',
              color: '#8a7f72',
              fontSize: 14,
              fontFamily: '-apple-system, system-ui',
            }}
          >
            {filterEmptyMessage[filter]}
          </div>
        )}
        {showMatches && upcoming.length > 0 && (
          <div>
            {showSectionHeadings && (
              <div style={{ padding: '4px 4px 10px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <VariantHeading variant={variant}>{t('saved:sections.matches')}</VariantHeading>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#9a9590' }}>
                  · {upcoming.length}
                </span>
              </div>
            )}
            {upcoming.map(renderPlanRow)}
          </div>
        )}
        {showEvents && upcomingEvents.length > 0 && (
          <div style={{ marginTop: showMatches && upcoming.length > 0 ? 26 : 0 }}>
            {showSectionHeadings && (
              <div style={{ padding: '4px 4px 10px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <VariantHeading variant={variant}>{t('saved:sections.events')}</VariantHeading>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#9a9590' }}>
                  · {upcomingEvents.length}
                </span>
              </div>
            )}
            {upcomingEvents.map(renderEventRow)}
          </div>
        )}
        {showBars && hasSaved && (
          <div
            style={{
              marginTop:
                showSectionHeadings && (hasPlans || hasEvents) ? 26 : 0,
            }}
          >
            {showSectionHeadings && (
              <div style={{ padding: '4px 4px 10px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <VariantHeading variant={variant}>{t('saved:sections.spots')}</VariantHeading>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#9a9590' }}>
                  · {savedVenues.length}
                </span>
              </div>
            )}
            {savedVenues.map(renderSavedVenue)}
          </div>
        )}
        {/* "Earlier" lives at the very bottom and is collapsed by default — a
            tap on the header reveals the past matches/events. */}
        {hasEarlier && (
          <div
            style={{
              marginTop:
                (showMatches && upcoming.length > 0) ||
                (showEvents && upcomingEvents.length > 0) ||
                (showBars && hasSaved)
                  ? 26
                  : 0,
            }}
          >
            <button
              type="button"
              onClick={() => {
                haptics.light();
                setEarlierOpen((v) => !v);
              }}
              aria-expanded={earlierOpen}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                width: '100%',
                padding: '4px 4px 10px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <VariantHeading variant={variant}>{t('saved:sections.earlier')}</VariantHeading>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#9a9590' }}>
                · {earlierCount}
              </span>
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9a9590"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  marginLeft: 'auto',
                  flexShrink: 0,
                  alignSelf: 'center',
                  transform: earlierOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {earlierOpen && (
              <div style={{ opacity: 0.65 }}>
                {showMatches && earlier.map(renderPlanRow)}
                {showEvents && earlierEvents.map(renderEventRow)}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ height: 100 }} />
      <SharePlanFlow request={shareRequest} onClose={() => setShareRequest(null)} />
      {accountSheetEl}
      <style>{SHARE_SPIN_KEYFRAMES}</style>
    </>
  );
}
